/**
 * aeterna/neural/dst.ts — Dialogue State Tracker
 *
 * The largest gap: what is this conversation *about*?
 *
 * Affect says how. Surprise says whether. Intent says what kind.
 * But none of them track the persistent belief state — the referent,
 * the topic, the conversational intent that carries across turns.
 *
 * This is a lightweight slot-filling DST. No external NLP. No POS tagger.
 * Instead: sovereign morphological analysis built from first principles.
 *
 * The Aeterna knows:
 *   - What topic we're on (room, personal, game, aeterna, idle)
 *   - What the player seems to want (advice, sharing, asking, playing)
 *   - What specific thing is being discussed (referent extraction)
 *   - How long we've been on this topic (topic persistence)
 *   - Whether the conversation feels like it's winding down
 *   - The integrated mood trajectory across turns
 *
 * This constructs the *context envelope* for EntityZero — keeping
 * the LLM grounded in what was actually happening, not hallucinating
 * a conversation that didn't occur.
 */

import {
  zeros, matvec, vadd, softmax, argmax, ema,
  randomMatrix, mulberry32, gaussianSeeded, cosine, normalize,
} from "./math.js";

// ─── Slot Types ──────────────────────────────────────────────────────────────

export const TOPIC_SLOTS = [
  "room_design",   // furniture, decoration, layout
  "personal",      // feelings, life, relationships
  "game",          // gameplay, mechanics, items, creatures
  "aeterna",       // about the Aeterna itself, philosophy
  "idle",          // small talk, no clear topic
] as const;
export type TopicSlot = typeof TOPIC_SLOTS[number];

export const INTENT_SLOTS = [
  "seeking_advice", // wants guidance
  "sharing",        // sharing experience/feeling
  "asking",         // direct question
  "playing",        // playful engagement
  "none",           // no clear intent
] as const;
export type IntentSlot = typeof INTENT_SLOTS[number];

// ─── Dialogue State ──────────────────────────────────────────────────────────

/** A topic thread with context about when it was active */
export interface TopicThread {
  topic: TopicSlot;
  referent: string | null;
  startTurn: number;
  lastActiveTurn: number;
  turnCount: number;
  /** Was this topic returned to after a departure? */
  returned: boolean;
  /** How many times has the player returned to this topic? */
  returnCount: number;
  /** Duration (in turns) of the most recent return visit */
  lastReturnDuration: number;
}

export interface DialogueState {
  topic: TopicSlot;
  topicConfidence: number;
  intent: IntentSlot;
  intentConfidence: number;
  referent: string | null;
  /** Integrated mood: 8-dim EMA of per-turn affect */
  mood: Float32Array;
  turnCount: number;
  /** How many turns on the current topic */
  topicPersistence: number;
  lastTopicChange: number;
  /** Is the conversation winding down? */
  windingDown: boolean;
  /** Engagement score: are responses getting shorter/longer? */
  engagement: number;
  /** Negation detected in last turn */
  negated: boolean;
  /** Question detected in last turn */
  isQuestion: boolean;
  /** Active topic stack (most recent first) */
  topicStack: TopicThread[];
  /** Did the player just return to a previous topic? */
  topicReturn: boolean;
  /** Topics the player has been approached on and deflected from */
  avoidedTopics: TopicSlot[];
  /** Was there a context switch this turn? */
  contextSwitch: boolean;
  /** Repair/correction detected: player is correcting a misunderstanding */
  repairDetected: boolean;
}

// ─── Topic Keywords (sovereign, no external NLP) ─────────────────────────────

const TOPIC_KEYWORDS: Record<TopicSlot, string[]> = {
  room_design: [
    "room", "furniture", "table", "chair", "lamp", "light", "cozy", "decorate",
    "design", "place", "move", "arrange", "shelf", "rug", "plant", "window",
    "wall", "floor", "layout", "aesthetic", "vibe", "feel", "atmosphere",
    "curtain", "bookshelf", "candle", "painting", "clock", "fireplace",
  ],
  personal: [
    "feel", "feeling", "sad", "happy", "tired", "lonely", "love", "miss",
    "remember", "dream", "hope", "afraid", "worried", "anxious", "day",
    "life", "friend", "family", "heart", "soul", "cry", "laugh", "think",
    "believe", "wish", "want", "need", "hurt", "care", "thank",
  ],
  game: [
    "play", "game", "item", "inventory", "craft", "build", "quest", "creature",
    "pet", "score", "level", "unlock", "collect", "recipe", "material",
    "currency", "coin", "shop", "buy", "sell", "trade", "rare", "common",
  ],
  aeterna: [
    "aeterna", "you", "alive", "conscious", "real", "mind", "think",
    "experience", "what are you", "who are you", "exist", "awareness",
    "sentient", "feel", "understand", "know", "mean", "purpose",
    "intelligence", "soul", "entity", "being", "self",
  ],
  idle: [
    "hello", "hi", "hey", "hmm", "ok", "yeah", "sure", "cool", "nice",
    "alright", "yep", "lol", "haha", "hm", "oh", "well", "whatever",
    "anyway", "meh", "idk", "dunno", "nah", "nope", "sup", "yo",
    "nothing", "nm", "fine", "same", "true", "right", "yea",
  ],
};

const INTENT_KEYWORDS: Record<IntentSlot, string[]> = {
  seeking_advice: [
    "should", "how", "what do", "help", "advice", "suggest", "recommend",
    "best", "better", "guide", "idea", "tip", "opinion",
  ],
  sharing: [
    "today", "just", "wanted to", "tell you", "happened", "found",
    "made", "saw", "noticed", "look", "check", "built",
  ],
  asking: [
    "what", "why", "how", "when", "where", "who", "does", "is", "are",
    "can", "could", "would", "do you", "have you",
  ],
  playing: [
    "haha", "lol", "bet", "dare", "try", "guess", "watch this",
    "funny", "joke", "silly", "let's", "game", "challenge",
  ],
  none: [],
};

// ─── Morphological Analysis (sovereign NLP) ──────────────────────────────────

/** Extract likely nouns (capitalized or preceded by articles/adjectives) */
function extractNouns(tokens: string[]): string[] {
  const articles = new Set(["the", "a", "an", "my", "your", "this", "that"]);
  const nouns: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const prev = i > 0 ? tokens[i - 1] : "";
    // After article → likely noun
    if (articles.has(prev) && t.length > 2) {
      nouns.push(t);
    }
    // Capitalized (not first word) → proper noun
    if (i > 0 && t[0] >= "A" && t[0] <= "Z") {
      nouns.push(t.toLowerCase());
    }
    // Common noun suffixes
    if (t.endsWith("tion") || t.endsWith("ment") || t.endsWith("ness") ||
        t.endsWith("ity")  || t.endsWith("ance") || t.endsWith("ence")) {
      nouns.push(t);
    }
  }
  return nouns;
}

/** Detect negation */
function hasNegation(tokens: string[]): boolean {
  const negWords = new Set([
    "not", "no", "never", "neither", "nobody", "nothing", "nowhere",
    "nor", "none", "don't", "doesn't", "didn't", "won't", "wouldn't",
    "can't", "couldn't", "shouldn't", "isn't", "aren't", "wasn't", "weren't",
  ]);
  // Also handle contractions like "don't" → ["don", "'t"]
  for (const t of tokens) {
    if (negWords.has(t)) return true;
    if (t.endsWith("n't")) return true;
  }
  return false;
}

/** Detect conversational repair: player is correcting Aeterna's misunderstanding */
function detectRepair(text: string, tokens: string[]): boolean {
  const lower = text.toLowerCase();

  // Direct corrections
  const repairPhrases = [
    "no i meant", "no, i meant", "that's not what i",
    "thats not what i", "i didn't mean", "i didn't say",
    "what i meant", "what i'm saying", "what i said was",
    "you misunderstood", "you misread", "you got that wrong",
    "not what i meant", "that's not it", "thats not it",
    "no no", "wait no", "i was talking about",
    "i'm talking about", "let me clarify", "to clarify",
    "i mean", "what i mean is",
  ];
  for (const phrase of repairPhrases) {
    if (lower.includes(phrase)) return true;
  }

  // Pattern: starts with "no" + correction verb
  if (tokens.length >= 3 && tokens[0] === "no") {
    const correctionVerbs = ["i", "it's", "its", "the", "that's", "what"];
    if (correctionVerbs.includes(tokens[1])) return true;
  }

  return false;
}

/** Detect question */
function isQuestion(text: string): boolean {
  if (text.trim().endsWith("?")) return true;
  const lower = text.toLowerCase().trim();
  const qWords = ["what", "why", "how", "when", "where", "who", "which",
                   "does", "do", "is", "are", "can", "could", "would", "will", "should"];
  for (const q of qWords) {
    if (lower.startsWith(q + " ")) return true;
  }
  return false;
}

/** Extract the most salient referent from text */
function extractReferent(tokens: string[]): string | null {
  // Priority: extracted nouns that aren't stop words
  const stopWords = new Set([
    "the", "a", "an", "is", "are", "was", "were", "be", "been", "being",
    "have", "has", "had", "do", "does", "did", "will", "would", "could",
    "should", "may", "might", "can", "shall", "i", "you", "he", "she",
    "it", "we", "they", "me", "him", "her", "us", "them", "my", "your",
    "his", "its", "our", "their", "this", "that", "these", "those",
    "and", "but", "or", "so", "if", "then", "than", "when", "while",
    "with", "from", "into", "about", "for", "of", "in", "on", "at", "to",
    "up", "out", "off", "over", "under", "just", "very", "really",
    "here", "there", "now", "then", "also", "too", "not", "no",
    // Contractions (tokenized with apostrophe preserved)
    "i'm", "i've", "i'll", "i'd", "it's", "that's", "he's", "she's",
    "we're", "they're", "you're", "you'll", "you've", "you'd", "we'd",
    "they'd", "there's", "here's", "what's", "who's", "how's", "where's",
    "don't", "doesn't", "didn't", "won't", "can't", "couldn't",
    "wouldn't", "shouldn't", "isn't", "aren't", "wasn't", "weren't",
    "haven't", "hasn't", "hadn't", "let's",
  ]);

  // Non-referential words that pass length filters but carry no semantic object.
  // These are verbs, adverbs, pronouns, and discourse markers — never a "thing."
  const junkWords = new Set([
    // Discourse markers / hedges
    "whatever", "actually", "basically", "honestly", "literally", "seriously",
    "probably", "definitely", "obviously", "apparently", "anyway", "maybe",
    "perhaps", "kinda", "sorta", "really", "pretty", "quite", "rather",
    // Common verbs (gerund/past that pass length filters)
    "telling", "feeling", "having", "going", "being", "doing", "getting",
    "making", "saying", "taking", "looking", "coming", "thinking", "knowing",
    "trying", "seeing", "asking", "working", "talking", "happening",
    // Indefinite pronouns
    "anything", "something", "everything", "nothing", "everyone", "someone",
    "nobody", "anybody", "somebody", "everywhere", "somewhere", "nowhere",
    // Contractions (tokenized)
    "didn't", "doesn't", "couldn't", "wouldn't", "shouldn't", "wasn't",
    "weren't", "aren't", "isn't", "haven't", "hasn't", "won't", "can't",
    // Filler / non-referential
    "sorry", "please", "thanks", "right", "wrong", "thing", "things",
    "stuff", "kind", "sort", "like", "even", "still", "always", "never",
    "much", "many", "some", "enough", "little", "well", "mean",
    // Temporal adverbs — common but never a semantic referent
    "lately", "recently", "today", "tomorrow", "yesterday", "tonight",
    "already", "finally", "sometimes", "usually", "often", "rarely",
    // Vague modifiers that pass length filters
    "specific", "certain", "several", "different", "various",
    "better", "worse", "great", "awful",
  ]);

  const nouns = extractNouns(tokens);
  for (const noun of nouns) {
    if (!stopWords.has(noun) && !junkWords.has(noun) && noun.length > 2) return noun;
  }

  // Fallback: longest non-junk, non-stop token (min 5 chars — short words are
  // almost never meaningful referents outside of noun extraction)
  let best: string | null = null;
  let bestLen = 0;
  for (const t of tokens) {
    if (!stopWords.has(t) && !junkWords.has(t) && t.length > bestLen && t.length > 4) {
      best = t;
      bestLen = t.length;
    }
  }
  return best;
}

// ─── Classifier ──────────────────────────────────────────────────────────────

const FEATURE_DIM = 88; // embed(64) + affect(8) + stateVec(16)
const N_TOPICS = TOPIC_SLOTS.length;
const N_INTENTS = INTENT_SLOTS.length;

export class DialogueStateTracker {
  private state: DialogueState;
  private history: DialogueState[] = [];

  /** Topic classifier: N_TOPICS × FEATURE_DIM */
  private topicW: Float32Array;
  private topicB: Float32Array;
  /** Intent classifier: N_INTENTS × FEATURE_DIM */
  private intentW: Float32Array;
  private intentB: Float32Array;

  /** Running token frequency for keyword boosting */
  private tokenFreq = new Map<string, number>();
  /** Message length history for engagement tracking */
  private messageLengths: number[] = [];
  /** Topic stack: all topic threads in this session */
  private topicStack: TopicThread[] = [];
  /** Topics the player has deflected from */
  private avoidedTopics: Set<TopicSlot> = new Set();
  /** Deflection detection: last topic + its turn */
  private lastDeflection: { topic: TopicSlot; turn: number } | null = null;

  constructor(seed = 7777) {
    this.topicW = randomMatrix(N_TOPICS, FEATURE_DIM, 1 / Math.sqrt(FEATURE_DIM), seed);
    this.topicB = zeros(N_TOPICS);
    this.intentW = randomMatrix(N_INTENTS, FEATURE_DIM, 1 / Math.sqrt(FEATURE_DIM), seed + 1);
    this.intentB = zeros(N_INTENTS);

    this.state = {
      topic: "idle",
      topicConfidence: 0,
      intent: "none",
      intentConfidence: 0,
      referent: null,
      mood: new Float32Array(8).fill(0.5),
      turnCount: 0,
      topicPersistence: 0,
      lastTopicChange: 0,
      windingDown: false,
      engagement: 0.5,
      negated: false,
      isQuestion: false,
      topicStack: [],
      topicReturn: false,
      avoidedTopics: [],
      contextSwitch: false,
      repairDetected: false,
    };
  }

  /**
   * Update dialogue state from a new player message.
   *
   * @param embedding  64-dim from embeddings.ts
   * @param affect     8-dim from charcnn.ts
   * @param text       raw message text
   */
  update(
    embedding: Float32Array,
    affect: Float32Array,
    text: string,
  ): DialogueState {
    const tokens = text.toLowerCase().replace(/[^\w\s'-]/g, " ").split(/\s+/).filter(t => t.length > 1);

    // ── Keyword scoring (symbolic, exact) ────────────────────────────────
    const topicScores = new Float32Array(N_TOPICS);
    for (let t = 0; t < N_TOPICS; t++) {
      const keywords = TOPIC_KEYWORDS[TOPIC_SLOTS[t]];
      for (const token of tokens) {
        if (keywords.includes(token)) topicScores[t] += 1;
        // Partial match for longer words
        for (const kw of keywords) {
          if (token.length > 4 && kw.length > 4 && token.includes(kw.slice(0, 4))) {
            topicScores[t] += 0.3;
          }
        }
      }
      // Normalize by keyword list size
      topicScores[t] /= Math.max(1, keywords.length) * 0.1;
    }

    const intentScores = new Float32Array(N_INTENTS);
    for (let t = 0; t < N_INTENTS; t++) {
      const keywords = INTENT_KEYWORDS[INTENT_SLOTS[t]];
      for (const token of tokens) {
        if (keywords.includes(token)) intentScores[t] += 1;
        for (const phrase of keywords) {
          if (phrase.includes(" ") && text.toLowerCase().includes(phrase)) {
            intentScores[t] += 2; // multi-word match is stronger
          }
        }
      }
      intentScores[t] /= Math.max(1, keywords.length) * 0.1;
    }

    // ── Neural scoring (learned, from features) ──────────────────────────
    const stateVec = this.encodeCurrentState();
    const features = new Float32Array(FEATURE_DIM);
    features.set(embedding, 0);
    features.set(affect, 64);
    features.set(stateVec, 72);

    const neuralTopicLogits = matvec(this.topicW, N_TOPICS, FEATURE_DIM, features);
    vadd(neuralTopicLogits, this.topicB, neuralTopicLogits);

    const neuralIntentLogits = matvec(this.intentW, N_INTENTS, FEATURE_DIM, features);
    vadd(neuralIntentLogits, this.intentB, neuralIntentLogits);

    // ── Blend symbolic + neural ──────────────────────────────────────────
    // Symbolic gets 70% weight (sovereign, deterministic, interpretable)
    // Neural gets 30% (learned adaptation over time)
    const blendedTopicLogits = new Float32Array(N_TOPICS);
    for (let i = 0; i < N_TOPICS; i++) {
      blendedTopicLogits[i] = topicScores[i] * 0.7 + neuralTopicLogits[i] * 0.3;
    }
    const blendedIntentLogits = new Float32Array(N_INTENTS);
    for (let i = 0; i < N_INTENTS; i++) {
      blendedIntentLogits[i] = intentScores[i] * 0.7 + neuralIntentLogits[i] * 0.3;
    }

    // Topic inertia: boost current topic slightly (conversations persist)
    const currentTopicIdx = TOPIC_SLOTS.indexOf(this.state.topic);
    if (currentTopicIdx >= 0) {
      blendedTopicLogits[currentTopicIdx] += 0.3 * this.state.topicConfidence;
    }

    const topicProbs = softmax(blendedTopicLogits);
    const intentProbs = softmax(blendedIntentLogits);

    const newTopicIdx = argmax(topicProbs);
    const newIntentIdx = argmax(intentProbs);
    const newTopic = TOPIC_SLOTS[newTopicIdx];
    const newIntent = INTENT_SLOTS[newIntentIdx];

    // ── Referent extraction ──────────────────────────────────────────────
    const referent = extractReferent(tokens);

    // ── Mood: EMA of affect ──────────────────────────────────────────────
    const mood = new Float32Array(this.state.mood);
    ema(mood, affect, 0.3);

    // ── Engagement tracking ──────────────────────────────────────────────
    this.messageLengths.push(tokens.length);
    if (this.messageLengths.length > 20) this.messageLengths.shift();
    const recentAvg = this.messageLengths.length > 3
      ? this.messageLengths.slice(-5).reduce((s, v) => s + v, 0) / 5
      : tokens.length;
    const overallAvg = this.messageLengths.reduce((s, v) => s + v, 0) / this.messageLengths.length;
    const engagement = Math.min(1, Math.max(0, 0.5 + (recentAvg - overallAvg) * 0.1));

    // ── Winding down detection ───────────────────────────────────────────
    const windingDown = (
      this.messageLengths.length >= 5 &&
      recentAvg < overallAvg * 0.6 &&
      engagement < 0.35
    );

    // ── Topic persistence & stack management ──────────────────────────
    const topicChanged = newTopic !== this.state.topic;
    const topicPersistence = topicChanged ? 0 : this.state.topicPersistence + 1;
    const turn = this.state.turnCount + 1;
    let topicReturn = false;
    let contextSwitch = false;

    if (topicChanged && newTopic !== "idle") {
      contextSwitch = true;

      // Check: is this a RETURN to a previous topic?
      const existingThread = this.topicStack.find(t => t.topic === newTopic);
      if (existingThread) {
        // Player returned to this topic — meaningful!
        existingThread.returned = true;
        existingThread.returnCount++;
        existingThread.lastActiveTurn = turn;
        topicReturn = true;
      } else {
        // New topic thread
        this.topicStack.push({
          topic: newTopic,
          referent,
          startTurn: turn,
          lastActiveTurn: turn,
          turnCount: 0,
          returned: false,
          returnCount: 0,
          lastReturnDuration: 0,
        });
      }

      // Cap stack depth
      if (this.topicStack.length > 8) this.topicStack.shift();

      // Deflection detection: require return + re-abandon.
      // A topic is only truly avoided if the player returned to it AND
      // left quickly again. First-time short visits are normal fast
      // conversation, not deflection. This prevents false-positive avoidance
      // flags for conversationally fluid players.
      if (this.state.topicPersistence <= 2 && this.state.topic !== "idle") {
        const prevThread = this.topicStack.find(t => t.topic === this.state.topic);
        if (prevThread && prevThread.returned && prevThread.returnCount > 0) {
          // They came back AND left quickly again — genuine avoidance signal
          this.avoidedTopics.add(this.state.topic);
          this.lastDeflection = { topic: this.state.topic, turn };
        }
        // Track the return duration for future checks
        if (prevThread) {
          prevThread.lastReturnDuration = this.state.topicPersistence;
        }
      }
    } else if (!topicChanged && newTopic !== "idle") {
      // Update current thread's turn count
      const currentThread = this.topicStack.find(t => t.topic === newTopic);
      if (currentThread) {
        currentThread.turnCount++;
        currentThread.lastActiveTurn = turn;
        // If they persist on a previously "avoided" topic, remove avoidance
        if (currentThread.turnCount >= 3) {
          this.avoidedTopics.delete(newTopic);
        }
      }
    }

    // ── Assemble new state ───────────────────────────────────────────────
    this.state = {
      topic: newTopic,
      topicConfidence: topicProbs[newTopicIdx],
      intent: newIntent,
      intentConfidence: intentProbs[newIntentIdx],
      // Referent persistence: carry forward ONLY when the current message
      // is too short to have its own referent (1-2 tokens = "yeah", "ok").
      // If the message is substantive (≥3 tokens) but produced no referent,
      // the player has moved on — don't show stale referents.
      // Also clear on topic change (obviously about something different).
      referent: referent ?? (
        topicChanged || tokens.length >= 3 ? null : this.state.referent
      ),
      mood,
      turnCount: turn,
      topicPersistence,
      lastTopicChange: topicChanged ? this.state.turnCount : this.state.lastTopicChange,
      windingDown,
      engagement,
      negated: hasNegation(tokens),
      isQuestion: isQuestion(text),
      topicStack: [...this.topicStack],
      topicReturn,
      avoidedTopics: [...this.avoidedTopics],
      contextSwitch,
      repairDetected: detectRepair(text, tokens),
    };

    this.history.push({
      ...this.state,
      mood: new Float32Array(mood),
      topicStack: [...this.topicStack],
      avoidedTopics: [...this.avoidedTopics],
    });
    if (this.history.length > 100) this.history.shift();

    // ── Online weight update (Hebbian) ───────────────────────────────────
    // Reinforce the connection between current features and detected topic
    this.onlineUpdate(features, newTopicIdx, newIntentIdx);

    return this.state;
  }

  /** Encode current state as a 16-dim vector for feature concatenation */
  private encodeCurrentState(): Float32Array {
    const v = zeros(16);
    v[TOPIC_SLOTS.indexOf(this.state.topic)] = 1;   // one-hot topic (5 dims)
    v[5 + INTENT_SLOTS.indexOf(this.state.intent)] = 1; // one-hot intent (5 dims)
    v[10] = this.state.topicPersistence / 20;   // normalized persistence
    v[11] = this.state.engagement;
    v[12] = this.state.windingDown ? 1 : 0;
    v[13] = this.state.turnCount / 50;          // normalized turn count
    v[14] = this.state.negated ? 1 : 0;
    v[15] = this.state.isQuestion ? 1 : 0;
    return v;
  }

  /** Hebbian online update: strengthen features→slot connections */
  private onlineUpdate(features: Float32Array, topicIdx: number, intentIdx: number): void {
    const lr = 0.001;
    // ΔW = lr * (target_one_hot - predicted) ⊗ features
    for (let d = 0; d < FEATURE_DIM; d++) {
      this.topicW[topicIdx * FEATURE_DIM + d] += lr * features[d];
      this.intentW[intentIdx * FEATURE_DIM + d] += lr * features[d];
    }
  }

  // ─── Queries ───────────────────────────────────────────────────────────

  /** Get current dialogue state */
  getState(): Readonly<DialogueState> { return this.state; }

  /** Get the full conversation topic trajectory */
  getTopicHistory(): TopicSlot[] {
    return this.history.map(h => h.topic);
  }

  /** How many topic changes have occurred? */
  getTopicChangeCount(): number {
    let changes = 0;
    for (let i = 1; i < this.history.length; i++) {
      if (this.history[i].topic !== this.history[i - 1].topic) changes++;
    }
    return changes;
  }

  /** Get the mood trajectory over time */
  getMoodTrajectory(): Float32Array[] {
    return this.history.map(h => new Float32Array(h.mood));
  }

  /** Is the conversation currently about the Aeterna itself? */
  isAboutAeterna(): boolean {
    return this.state.topic === "aeterna" && this.state.topicConfidence > 0.4;
  }

  /** Build a compact context string for EntityZero */
  buildContextEnvelope(): string {
    const s = this.state;
    const lines: string[] = [];
    lines.push(`Topic: ${s.topic} (${(s.topicConfidence * 100).toFixed(0)}%, ${s.topicPersistence} turns)`);
    lines.push(`Intent: ${s.intent} (${(s.intentConfidence * 100).toFixed(0)}%)`);
    if (s.referent) lines.push(`Discussing: ${s.referent}`);
    if (s.isQuestion) lines.push("Player is asking a question");
    if (s.negated) lines.push("Message contains negation");
    if (s.repairDetected) lines.push("Player is correcting a misunderstanding — re-read carefully");
    if (s.windingDown) lines.push("Conversation winding down");
    lines.push(`Engagement: ${(s.engagement * 100).toFixed(0)}%`);
    // Topic stack context
    if (s.topicReturn) {
      lines.push(`Player returned to topic: ${s.topic} (they keep coming back to this)`);
    }
    if (s.contextSwitch && !s.topicReturn) {
      lines.push(`Topic switch — player changed subject`);
    }
    if (s.avoidedTopics.length > 0) {
      lines.push(`Avoided topics: ${s.avoidedTopics.join(", ")} (player deflected from these)`);
    }
    // Show active threads (excluding idle)
    const activeThreads = s.topicStack.filter(t => t.topic !== "idle" && t.turnCount > 0);
    if (activeThreads.length > 1) {
      const threadSummary = activeThreads
        .map(t => `${t.topic}${t.returned ? " (returned)" : ""}:${t.turnCount}t`)
        .join(", ");
      lines.push(`Active threads: ${threadSummary}`);
    }
    return lines.join("\n");
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      state: {
        ...this.state,
        mood: Array.from(this.state.mood),
      },
      topicW: Array.from(this.topicW),
      topicB: Array.from(this.topicB),
      intentW: Array.from(this.intentW),
      intentB: Array.from(this.intentB),
    });
  }

  loadState(json: string): void {
    const data = JSON.parse(json);
    if (data.state) {
      this.state = {
        ...data.state,
        mood: new Float32Array(data.state.mood),
      };
    }
    if (data.topicW) this.topicW = new Float32Array(data.topicW);
    if (data.topicB) this.topicB = new Float32Array(data.topicB);
    if (data.intentW) this.intentW = new Float32Array(data.intentW);
    if (data.intentB) this.intentB = new Float32Array(data.intentB);
  }
}
